import React, { useEffect, useReducer } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { articlesAPI } from "../services/api";
import { CATEGORIES } from "../lib/categories";
import ArticleCard from "../components/ArticleCard";
import Seo from "../components/Seo";
import "./Home.css";

const initialHome = { featured: null, articles: [], loading: true };
function homeReducer(state, action) {
  if (action.type === "ok") return { featured: action.featured, articles: action.articles, loading: false };
  if (action.type === "err") return { ...state, loading: false };
  return state;
}

export default function Home() {
  const [{ featured, articles, loading }, dispatch] = useReducer(homeReducer, initialHome);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [f, list] = await Promise.all([
          articlesAPI.featured().catch(() => ({ data: null })),
          articlesAPI.list({ page_size: 9 }),
        ]);
        if (cancelled) return;
        dispatch({ type: "ok", featured: f.data, articles: list.data.items || [] });
      } catch (e) {
        if (!cancelled) dispatch({ type: "err" });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="home">
        <div className="loading-container"><div className="loading-spinner" /></div>
      </div>
    );
  }

  const remaining = featured ? articles.filter((a) => a.slug !== featured.slug) : articles;
  const latestGrid = remaining.slice(0, 6);

  return (
    <div className="home" data-testid="home-page">
      <Seo
        title="Motorsport News, Analysis & Opinion"
        description="Race377 covers Formula 1, WEC, IMSA, GT World Challenge, Formula E, IndyCar, NASCAR and the business of motorsport. Independent journalism, sharp opinion, race-week analysis."
        type="website"
      />

      {/* Tagline strip */}
      <section className="home-hero">
        <div className="container">
          <span className="caption home-hero__eyebrow">Race377 — Motorsport Journalism</span>
          <h1 className="brand-display home-hero__title">
            Speed.<br />Stories.<br />Substance.
          </h1>
          <p className="body-large home-hero__sub">
            Independent reporting on Formula 1, WEC, IMSA, GT, Formula E, IndyCar, NASCAR and the business of racing.
          </p>
        </div>
      </section>

      {/* Featured article */}
      {featured && (
        <section className="home-section">
          <div className="container">
            <div className="section-header">
              <h2 className="heading-2">Featured</h2>
              <Link to="/news" className="section-link" data-testid="home-view-all-news">
                All news <ArrowRight size={18} />
              </Link>
            </div>
            <div className="home-featured">
              <ArticleCard article={featured} variant="hero" />
            </div>
          </div>
        </section>
      )}

      {/* Latest grid */}
      <section className="home-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Latest</h2>
          </div>
          {latestGrid.length === 0 ? (
            <p className="body-small">No articles yet — check back soon.</p>
          ) : (
            <div className="home-grid">
              {latestGrid.map((a) => <ArticleCard key={a.id} article={a} />)}
            </div>
          )}
        </div>
      </section>

      {/* Categories */}
      <section className="home-section home-section--alt">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Browse by series</h2>
          </div>
          <div className="home-categories">
            {CATEGORIES.map((c) => (
              <Link
                key={c.slug}
                to={`/categories/${c.slug}`}
                className="home-category-tile"
                data-testid={`home-category-${c.slug}`}
              >
                <span className="home-category-tile__name">{c.name}</span>
                <ArrowRight size={18} />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
